import { useEffect, useMemo, useRef, useState } from 'react';
import React from 'react';
import { useMenu } from '@happyspace/sdk/restaurant';
import { Search, X, ShoppingBag, Store, Clock } from 'lucide-react';
import PageHero from '../components/PageHero';
import DishCard, { categoryIcon } from '../components/DishCard';
import { useCartUI } from '../components/Layout';
import { useRestaurantCart } from '@happyspace/sdk/restaurant';
import { SITE, sortCategories } from '../lib/site';

export default function MenuPage() {
  const { items, categories, loading, error } = useMenu();
  const { itemCount } = useRestaurantCart();
  const { openCart } = useCartUI();
  const [query, setQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');

  const stickyBarRef = useRef<HTMLDivElement | null>(null);
  const sectionRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const categoryBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const menuTopRef = useRef<HTMLDivElement | null>(null);
  const isProgrammaticScroll = useRef(false);

  // Update page title and meta tags
  React.useEffect(() => {
    document.title = 'Yalla Eat Menu - Halal Shawarma, Gyros & Wings | Fresh to Order';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Browse Yalla Eat\'s full menu of 100% halal shawarma, NYC-style gyro platters, burgers, wings and family combos. Fresh ingredients, authentic recipes, ready in ~20 minutes.');
  }, []);

  const visibleCategories = useMemo(
    () => sortCategories(categories.filter((c) => items.some((i) => i.category === c.name))),
    [categories, items],
  );

  /** Search narrows the results; otherwise every category is grouped and shown together so the rail can jump between them. */
  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const matches = q
      ? items.filter(
          (i) =>
            i.title.toLowerCase().includes(q) ||
            (i.description || '').toLowerCase().includes(q) ||
            (i.category || '').toLowerCase().includes(q),
        )
      : items;

    const names = visibleCategories.map((c) => c.name);

    return names
      .map((name) => ({ name, items: matches.filter((i) => i.category === name) }))
      .filter((g) => g.items.length > 0);
  }, [items, visibleCategories, query]);

  const resultCount = groups.reduce((n, g) => n + g.items.length, 0);

  // Scroll-spy: highlight the category chip for whichever section is currently in view.
  useEffect(() => {
    if (query) return;
    const headerEl = document.querySelector('header');
    const headerH = headerEl?.getBoundingClientRect().height ?? 80;
    const barH = stickyBarRef.current?.getBoundingClientRect().height ?? 96;
    const topOffset = headerH + barH;

    const observer = new IntersectionObserver(
      (entries) => {
        if (isProgrammaticScroll.current) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const top = visible[0];
        const name = top?.target.getAttribute('data-category');
        if (name) setActiveCategory(name);
      },
      { rootMargin: `-${Math.round(topOffset)}px 0px -55% 0px`, threshold: [0, 0.1] },
    );

    Object.values(sectionRefs.current).forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [groups, query]);

  // Keep the active chip visible inside the horizontally scrolling rail.
  useEffect(() => {
    categoryBtnRefs.current[activeCategory]?.scrollIntoView({
      behavior: 'smooth',
      inline: 'center',
      block: 'nearest',
    });
  }, [activeCategory]);

  const scrollToCategory = (name: string) => {
    setQuery('');
    setActiveCategory(name);
    isProgrammaticScroll.current = true;

    const target = name === 'all' ? menuTopRef.current : sectionRefs.current[name];
    if (target) {
      const headerEl = document.querySelector('header');
      const headerH = headerEl?.getBoundingClientRect().height ?? 80;
      const barH = stickyBarRef.current?.getBoundingClientRect().height ?? 96;
      const top = target.getBoundingClientRect().top + window.scrollY - headerH - barH - 16;
      window.scrollTo({ top, behavior: 'smooth' });
    }

    window.setTimeout(() => {
      isProgrammaticScroll.current = false;
    }, 700);
  };

  return (
    <>
      <PageHero
        eyebrow="Menu"
        title="Our Full"
        highlight="Menu"
        intro={`Every dish is 100% halal, prepared fresh to order. ${items.length || ''} dishes across shawarma, gyros, platters, burgers, wings and more — add what you like to your cart and pick it up in about ${SITE.pickupMinutes} minutes.`}
      />

      {/* Ordering info bar */}
      <div style={{ background: 'var(--color-cream)' }}>
        <div className="max-w-screen-xl mx-auto px-5 md:px-8 py-4 flex flex-wrap items-center gap-x-8 gap-y-2 text-[13px] text-ink-2">
          <span className="inline-flex items-center gap-2">
            <Store size={15} style={{ color: 'var(--color-ink)' }} />
            Pickup only &mdash; {SITE.addressLines[0]}
          </span>
          <span className="inline-flex items-center gap-2">
            <Clock size={15} style={{ color: 'var(--color-ink)' }} />
            Ready in ~{SITE.pickupMinutes} min
          </span>
        </div>
      </div>

      {/* Sticky search + category rail — stays put under the header while the menu scrolls */}
      <div
        ref={stickyBarRef}
        className="sticky top-[72px] md:top-20 z-30 border-b bg-white"
        style={{ borderColor: 'var(--color-line)' }}
      >
        <div className="max-w-screen-xl mx-auto px-5 md:px-8 py-4 flex flex-col gap-4">
          <div className="relative max-w-md">
            <Search
              size={17}
              className="absolute left-4 top-1/2 -translate-y-1/2 text-ink-2 pointer-events-none"
            />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the menu…"
              aria-label="Search the menu"
              className="w-full rounded-md border-2 bg-white pl-11 pr-10 py-3 text-sm outline-none transition-colors focus:border-[var(--color-yellow)]"
              style={{ borderColor: 'var(--color-line)' }}
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                aria-label="Clear search"
                className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md flex items-center justify-center text-ink-2 hover:text-ink"
              >
                <X size={15} />
              </button>
            )}
          </div>

          {visibleCategories.length > 0 && (
            <div className="flex gap-2.5 overflow-x-auto pb-1 -mx-1 px-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {[{ _id: 'all', name: 'All' }, ...visibleCategories].map((cat) => {
                const key = cat._id === 'all' ? 'all' : cat.name;
                const Icon = categoryIcon(cat.name);
                const isActive = activeCategory === key && !query;
                return (
                  <button
                    key={cat._id}
                    ref={(el) => { categoryBtnRefs.current[key] = el; }}
                    onClick={() => scrollToCategory(key)}
                    className="inline-flex items-center gap-2 rounded-md border-2 px-4 py-2 text-xs font-extrabold uppercase tracking-wide transition-all duration-200 shrink-0 whitespace-nowrap"
                    style={
                      isActive
                        ? {
                            background: 'var(--color-ink)',
                            borderColor: 'var(--color-ink)',
                            color: '#fff',
                          }
                        : { background: '#fff', borderColor: 'var(--color-line)', color: 'var(--color-ink)' }
                    }
                  >
                    <Icon size={14} style={{ color: isActive ? 'var(--color-yellow)' : 'var(--color-ink)'}} />
                    {cat.name}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <section className="pt-6 md:pt-7 pb-8 md:pb-12 bg-white">
        <div ref={menuTopRef} className="max-w-screen-xl mx-auto px-5 md:px-8">
          {/* States */}
          {error && (
            <p className="mt-12 text-center text-ink-2">
              We couldn&apos;t load the menu just now. Please refresh, or call us to order.
            </p>
          )}

          {loading && (
            <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className="h-[380px] rounded-lg border animate-pulse"
                  style={{ borderColor: 'var(--color-line)', background: 'var(--color-cream)' }}
                />
              ))}
            </div>
          )}

          {!loading && !error && resultCount === 0 && (
            <div className="mt-16 text-center">
              <p className="display text-2xl text-ink">No dishes found</p>
              <p className="mt-2 text-sm text-ink-2">
                {query ? (
                  <>Nothing matches &ldquo;{query}&rdquo;. Try another search.</>
                ) : (
                  <>This category is empty right now.</>
                )}
              </p>
            </div>
          )}

          {/* Grouped dishes */}
          {!loading &&
            groups.map((group) => (
              <div
                key={group.name}
                ref={(el) => { sectionRefs.current[group.name] = el; }}
                data-category={group.name}
                className="mt-10 first:mt-6 scroll-mt-[220px]"
                id={group.name}
              >
                <div className="flex items-baseline justify-between gap-4 border-b pb-3" style={{ borderColor: 'var(--color-line)' }}>
                  <h2 className="display text-2xl md:text-3xl text-ink">{group.name}</h2>
                  <span className="text-[12px] font-bold uppercase tracking-wide text-ink-2 shrink-0">
                    {group.items.length} item{group.items.length === 1 ? '' : 's'}
                  </span>
                </div>
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
                  {group.items.map((item, i) => (
                    <DishCard key={item._id} item={item} index={i} />
                  ))}
                </div>
              </div>
            ))}
        </div>
      </section>

      {/* Sticky cart bar once something is in the cart */}
      {itemCount > 0 && (
        <div className="sticky bottom-0 z-40 border-t" style={{ background: 'var(--color-ink)', borderColor: 'rgba(255,255,255,0.1)' }}>
          <div className="max-w-screen-xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between gap-4">
            <p className="text-[13px] font-bold uppercase tracking-wide text-white">
              {itemCount} item{itemCount === 1 ? '' : 's'} in your order
            </p>
            <button onClick={openCart} className="btn-yellow !py-2.5 !px-5">
              <ShoppingBag size={15} /> View Cart
            </button>
          </div>
        </div>
      )}

      {/* JSON-LD: AggregateOffer for all menu items */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: `{\n        "@context": "https://schema.org",\n        "@type": "AggregateOffer",\n        "@id": "https://yallaeathalalfood.com/menu",\n        "name": "Yalla Eat Full Menu",\n        "description": "100% halal shawarma, gyro platters, burgers, wings, and family combos—all made fresh to order.",\n        "priceCurrency": "USD",\n        "lowPrice": "8",\n        "highPrice": "45",\n        "offerCount": ${items.length || 0},\n        "url": "https://yallaeathalalfood.com/menu",\n        "offers": [\n          ${items.slice(0, 5).map(item => `{\n            "@type": "Offer",\n            "@id": "https://yallaeathalalfood.com/menu#${item._id}",\n            "name": "${item.title.replace(/"/g, '\\\\"')}",\n            "description": "${(item.description || '').substring(0, 100).replace(/"/g, '\\\\"')}",\n            "category": "${item.category}",\n            "priceCurrency": "USD",\n            "price": "${item.price || 0}",\n            "seller": {\n              "@type": "Organization",\n              "name": "Yalla Eat"\n            }\n          }`).join(',')}\n        ]\n      }` }} />
    </>
  );
}
